/* eslint-disable max-lines */
import { createReducer } from 'typesafe-actions';
import { formatPrice } from '@westwing/appshell-formatting-helpers';
import { defaultErrorHandler } from '../dataFetching/generators';
import { ProductReviewsActions, ProductReviewsActionsType } from '../productReviews/action';
import { productReviewsReducer, defaultProductReviewState } from '../productReviews/reducer';
import {
    ProductDetailsActionsType,
    fetchProductDetailsActions,
    changeProductDetailSizeAction,
    setPDPProductKnownInfoAction,
    setTrackingFlag,
    setSelectedQuantity,
    fetchProductSimpleVideoActions,
    setIsBisrSubscribed,
    setSizeSelectorClicked,
    setSizeSelectorIsOpen,
    setFromPLPWithMood,
} from './actions';
import { ProductDetailState, Simple } from './types';
import { getCheapestSimple, getPriceFormatOptions, reduceDeliveryInformation, reduceToProductDetail } from './helper';
import getProductDetailPlaceHolder from './productDetailPlaceholder';
import galleryReducer, {
    galleryActions,
    GalleryActionsType,
    galleryDefaultState,
    set as setGallerySlides,
    preloadFirstImage,
} from './gallery';
import {
    colorVariantsActions,
    colorVariantsReducer,
    colorVariantsDefaultState,
    ColorVariantsActionsType,
    getColorVariants,
} from './colorVariants';
import { colorHoverActions, colorHoverDefaultState, colorHoverReducer, ColorHoverActionType } from './colorHover';
import { OrderFabricSampleAllActions, orderFabricSampleReducer, OrderFabricSampleActionsType } from './fabricSample';
import {
    reducer as threeDImageReducer,
    defaultThreeDImageState,
    actions as threeDImageActions,
    ThreeDImageActionsType,
} from './threeDImage';
import assemblyServiceReducer, { AssemblyServiceActions, AssemblyServiceActionsType } from './assemblyService';
import { get } from 'Common/utils/objectUtils';

export const trackingActionDefaultState = {
    videoLoad: false,
    videoPlay: false,
    videoPercent: 0,
};

export const userActionsDefaultState = {
    selectedQuantity: 1,
};

const defaultProductsState: ProductDetailState = {
    colorHover: colorHoverDefaultState,
    colorVariants: colorVariantsDefaultState,
    didInvalidate: false,
    gallery: galleryDefaultState,
    identifier: '',
    isFetching: false,
    productdetail: getProductDetailPlaceHolder(),
    reviews: defaultProductReviewState,
    trackingActions: trackingActionDefaultState,
    userActions: userActionsDefaultState,
    threeDImage: defaultThreeDImageState,
    orderFabricSample: {
        isFetching: false,
        success: false,
        validationErrors: {},
    },
};

type CombineProductDetailActions =
    | ProductDetailsActionsType
    | GalleryActionsType
    | ColorVariantsActionsType
    | ColorHoverActionType
    | ProductReviewsActionsType
    | OrderFabricSampleActionsType
    | AssemblyServiceActionsType
    | ThreeDImageActionsType;

const defaultStates = {
    colorHover: colorHoverDefaultState,
    trackingActions: trackingActionDefaultState,
    userActions: userActionsDefaultState,
};

const productDetailReducer = createReducer<ProductDetailState, CombineProductDetailActions>(defaultProductsState)
    .handleAction([...galleryActions], (state, action) => {
        return { ...state, gallery: galleryReducer(state.gallery, action) };
    })
    .handleAction(fetchProductDetailsActions.success, (state, action) => {
        const pdpData = get(action, 'payload.data.data');
        const { reducerPayloadOptions, shopDeliveryTimePromise } = action.payload;
        const defaultSimpleSku = get(pdpData, 'meta.default_simple_sku');
        const cheapestSimple = getCheapestSimple(defaultSimpleSku, get(pdpData, 'simples'));
        const productdetail = reduceToProductDetail({
            pdpData,
            formatOptions: getPriceFormatOptions(reducerPayloadOptions),
            cheapestSimple,
            shopDeliveryTimePromise,
        });
        return {
            ...state,
            colorVariants: colorVariantsReducer(
                state.colorVariants,
                getColorVariants(productdetail.sku, cheapestSimple)
            ),
            gallery: galleryReducer(
                state.gallery,
                setGallerySlides({
                    rawImagesPart1: cheapestSimple.slider_images,
                    rawImagesPart2: cheapestSimple.slider,
                    rawVideo: cheapestSimple.video,
                    isTwoManHandling: productdetail.deliveryInfo ? productdetail.deliveryInfo.twoManHandling : false,
                    isMoodImageFirstImage:
                        productdetail.isMoodImageFirstImage && reducerPayloadOptions?.p100PdpShowMoodImageFirst,
                })
            ),
            isFetching: false,
            isFetchingComplete: true,
            productdetail,
            pdpApiData: pdpData,
            reducerPayloadOptions,
            invalidSimpleSku: pdpData && pdpData.simples && !pdpData.simples[defaultSimpleSku],
            ...defaultStates,
        };
    })
    .handleAction(fetchProductDetailsActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(fetchProductDetailsActions.request, (state, action) => {
        const { imageUrl, sizes } = getProductDetailPlaceHolder();
        return {
            ...state,
            didInvalidate: false,
            error: undefined,
            identifier: action.payload.identifier,
            isFetching: true,
            productdetail: {
                ...state.productdetail,
                imageUrl,
                sizes,
            },
            invalidSimpleSku: false,
        };
    })
    .handleAction(fetchProductSimpleVideoActions.success, (state, action) => {
        const simpleVideo = get(action, 'payload.data.data');
        const simpleSku = get(action, 'payload.data.identifier');
        const newState = {
            ...state,
            isFetching: false,
        };

        if (simpleVideo && simpleSku && state.pdpApiData) {
            const selectedSimple = <Simple>get(state.pdpApiData || {}, `simples.${simpleSku}.meta`, {});
            const simpleWithVideoData = {
                ...selectedSimple,
                video: simpleVideo,
            };

            newState.pdpApiData = {
                ...state.pdpApiData,
                simples: {
                    ...state.pdpApiData.simples,
                    [simpleSku]: {
                        meta: {
                            ...simpleWithVideoData,
                        },
                    },
                },
            };
        }

        return newState;
    })
    .handleAction(fetchProductSimpleVideoActions.request, (state, action) => {
        return {
            ...state,
            isFetching: true,
            didInvalidate: false,
            identifier: action.payload.identifier,
        };
    })
    .handleAction(fetchProductSimpleVideoActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(setPDPProductKnownInfoAction, (state, { payload: { product, p100PdpShowMoodImageFirst } }) => {
        const { name, price, originalPrice, brand, productImage, isSustainable } = product;
        const { path, tag, seoUrl } = productImage;
        // Do not preload the first product image when we have a mood image first on pdp.
        const gallery = p100PdpShowMoodImageFirst
            ? {}
            : { gallery: galleryReducer(defaultProductsState.gallery, preloadFirstImage({ seoUrl, path, tag })) };
        const productdetail = {
            ...defaultProductsState.productdetail,
            name,
            originalPrice,
            price,
            brand: {
                name: brand,
                urlKey: '',
                description: '',
            },
            isSustainable,
        };
        return {
            ...defaultProductsState,
            fromPlpWithMood: state.fromPlpWithMood,
            productdetail,
            ...gallery,
        };
    })
    .handleAction(setTrackingFlag, (state, { payload: { event, percent } }) => {
        const value = percent || true;
        return {
            ...state,
            trackingActions: {
                ...state.trackingActions,
                [event]: value,
            },
        };
    })
    .handleAction(setSelectedQuantity, (state, { payload: { selectedQuantity } }) => {
        return {
            ...state,
            userActions: {
                ...state.userActions,
                selectedQuantity,
            },
        };
    })
    .handleAction(setFromPLPWithMood, (state, { payload: { fromPlpWithMood } }) => {
        return {
            ...state,
            fromPlpWithMood,
        };
    })
    .handleAction(
        changeProductDetailSizeAction,
        (state, { payload: { simple, p100PdpShowMoodImageFirst, shopDeliveryTimePromise } }) => {
            const selectedSimple = <Simple>get(state.pdpApiData || {}, `simples.${simple}.meta`, {});
            const { countryCode, currency } = getPriceFormatOptions(state.reducerPayloadOptions);
            const { productdetail } = state;
            const deliveryInfo = reduceDeliveryInformation({ simple: selectedSimple, shopDeliveryTimePromise });
            return {
                ...state,
                productdetail: {
                    ...productdetail,
                    originalPrice: formatPrice(countryCode, currency, selectedSimple.original_price),
                    price: formatPrice(countryCode, currency, selectedSimple.price),
                    numericPrice: selectedSimple.price,
                    isAvailable: selectedSimple.available,
                    selectedSize: selectedSimple.size,
                    activeSimpleSKU: simple,
                    imageUrl: selectedSimple.image_url,
                    deliveryInfo,
                    isBisrEnabled: selectedSimple.bisr_enabled,
                    isBisrSubscribed: selectedSimple.is_user_subscribed,
                    innerDetails: {
                        color: selectedSimple.product_details.pdp_color,
                        material: selectedSimple.product_details.pdp_material,
                        details: selectedSimple.product_details.pdp_details,
                        measures: selectedSimple.product_details.pdp_measures,
                        manufacturingMethod: selectedSimple.product_details.pdp_manufacturing_method,
                        underfloorHeating: selectedSimple.product_details.pdp_underfloor_heating,
                        packingDimensions: selectedSimple.product_details.pdp_packing_dimensions,
                    },
                    hasAddonProducts: !!selectedSimple.has_addon_product,
                },
                gallery: galleryReducer(
                    state.gallery,
                    setGallerySlides({
                        rawImagesPart1: selectedSimple.slider_images,
                        rawImagesPart2: selectedSimple.slider,
                        rawVideo: selectedSimple.video,
                        isTwoManHandling: deliveryInfo ? deliveryInfo.twoManHandling : false,
                        isMoodImageFirstImage: productdetail.isMoodImageFirstImage && p100PdpShowMoodImageFirst,
                    })
                ),
                currentIndex: 0,
                simpleSkuIdentifier: simple,
                invalidSimpleSku: state.pdpApiData && state.pdpApiData.simples && !state.pdpApiData.simples[simple],
                ...defaultStates,
            };
        }
    )
    .handleAction([...colorVariantsActions], (state, action) => {
        return { ...state, colorVariants: colorVariantsReducer(state.colorVariants, action) };
    })
    .handleAction([...colorHoverActions], (state, action) => {
        return { ...state, colorHover: colorHoverReducer(state.colorHover, action) };
    })
    .handleAction([...ProductReviewsActions], (state, action) => {
        return { ...state, reviews: productReviewsReducer(state.reviews, action) };
    })
    .handleAction([...OrderFabricSampleAllActions], (state, action) => {
        return { ...state, orderFabricSample: orderFabricSampleReducer(state.orderFabricSample, action) };
    })
    .handleAction([...threeDImageActions], (state, action) => {
        return { ...state, threeDImage: threeDImageReducer(state.threeDImage, action) };
    })
    .handleAction([...AssemblyServiceActions], (state, action) => {
        return {
            ...state,
            productdetail: {
                ...state.productdetail,
                assemblyServiceProduct: assemblyServiceReducer(state.productdetail.assemblyServiceProduct, action),
            },
        };
    })
    .handleAction(setIsBisrSubscribed, state => ({
        ...state,
        productdetail: {
            ...state.productdetail,
            isBisrSubscribed: true,
        },
    }))
    .handleAction(setSizeSelectorClicked, (state, { payload: { sizeSelectorClicked } }) => ({
        ...state,
        sizeSelectorClicked,
    }))
    .handleAction(setSizeSelectorIsOpen, (state, { payload: { sizeSelectorIsOpen } }) => ({
        ...state,
        sizeSelectorIsOpen,
    }));

export default productDetailReducer;
